import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { Separator } from '@ophelos/ui/components/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@ophelos/ui/components/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { ChevronLeft, LifeBuoy, PanelLeft, Plus, Presentation } from 'lucide-react';
import { useMemo } from 'react';
import { useHubNavigate, useHubPathname } from '@/lib/global/hub-path';
import { useDemoModeAccess } from '@/lib/presentation-mode/access';
import { usePresentationMode } from '@/lib/presentation-mode/store';
import { useHub } from '../../contexts/HubContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import type { AppNavGroup } from '../../lib/app-navigation';
import { filterNavItemsByAllClientsAccess, filterNavItemsByRole } from '../../lib/app-navigation';
import { SidebarContextSwitcher } from '../context/SidebarContextSwitcher';
import { UserMenu } from '../user/UserMenu';
import { DemoModeSwitch } from './DemoModeSwitch';
import { HubNavLink } from './HubNavLink';
import { SidebarLogo } from './SidebarLogo';

export function AppSidebar({ appName, groups }: { appName?: string; groups: AppNavGroup[] }) {
  const pathname = useHubPathname();
  const { state, toggleSidebar } = useSidebar();
  const navigate = useHubNavigate();
  const { roles, hasPermission, hasAnyRole, isSystemAdmin } = useUserPermissions();
  const { hasAllClientsAccess } = useHub();
  const canWriteIngestionSettings = isSystemAdmin || hasPermission('config:admin');
  const { showInAppSidebar } = usePresentationMode();
  const { allowed: canUseDemoMode } = useDemoModeAccess();
  const showDemoMode = appName === 'Analytics' && showInAppSidebar && canUseDemoMode;

  const filteredGroups = useMemo(() => {
    const byRole = filterNavItemsByRole(groups, roles);
    // All-clients filter applies beyond Finances (Admin pools/balance, Settings payments).
    const byClientAccess = filterNavItemsByAllClientsAccess(byRole, hasAllClientsAccess);
    return byClientAccess.filter((group) => group.items.length > 0);
  }, [groups, roles, hasAllClientsAccess]);

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="flex flex-row w-full h-13 gap-0 items-center py-0 justify-between">
        <SidebarLogo />
        {state === 'expanded' && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={toggleSidebar}
            className="hover:bg-sidebar-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          >
            <PanelLeft className="size-4 text-muted-foreground" />
          </Button>
        )}
      </SidebarHeader>

      <div className={`my-2 ${state === 'collapsed' ? 'px-2' : ''}`}>
        <SidebarContextSwitcher collapsed={state === 'collapsed'} />
      </div>

      <SidebarContent>
        <ScrollArea className="h-full [&>[data-radix-scroll-area-viewport]>div]:block!">
          <SidebarGroup className="pb-0">
            <SidebarMenuItem className="list-none">
              <SidebarMenuButton
                asChild
                tooltip="All Apps"
                className="p-0! mt-2 text-muted-foreground dark:text-muted-foreground! justify-start hover:bg-transparent! mb-2 active:shadow-none active:bg-muted/75!"
              >
                <HubNavLink to="/apps">
                  <ChevronLeft />
                  <span>All Apps</span>
                </HubNavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarGroup>

          {filteredGroups.map((group, index) => (
            <SidebarGroup key={group.label || `group-${index}`}>
              {index > 0 && !group.label && <Separator className="my-2" />}
              {group.label && (
                <SidebarGroupLabel className="mb-1 text-muted-foreground text-xs font-medium tracking-wide">
                  {group.label}
                  <Separator orientation="horizontal" className="flex-1 ml-2 bg-border" />
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => {
                    // For root-level routes (like '/app/ingestion'), only match exact path
                    // For sub-routes (like '/app/ingestion/configurations'), match path and sub-paths
                    const itemPath = item.to.split('?')[0];
                    const isRootRoute = /^\/app\/(ingestion|customers|campaigns|data|analytics|finances|ai|library)$/.test(itemPath);
                    const isActive = isRootRoute ? pathname === itemPath : pathname === itemPath || pathname.startsWith(`${itemPath}/`);
                    if (item.disabled) {
                      const disabledRow = (
                        <div className="peer/menu-button flex w-full items-center gap-2 overflow-hidden text-left outline-hidden h-8 text-sm rounded-md text-muted-foreground opacity-50 p-2">
                          <item.icon className="size-4" />
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap">{item.name}</span>
                          {item.comingSoon && (
                            <Badge variant="subtle" size="sm" className="bg-muted-foreground/10 border-transparent truncate">
                              Coming soon
                            </Badge>
                          )}
                        </div>
                      );
                      if (!item.disabledReason) {
                        return <div key={item.to}>{disabledRow}</div>;
                      }
                      return (
                        <div key={item.to}>
                          <Tooltip>
                            <TooltipTrigger asChild>{disabledRow}</TooltipTrigger>
                            <TooltipContent side="right" align="center" className="whitespace-nowrap">
                              <p>{item.disabledReason}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      );
                    }
                    return (
                      <SidebarMenuItem key={item.to} className="group/item relative">
                        <SidebarMenuButton asChild isActive={isActive} tooltip={item.name}>
                          <HubNavLink to={item.to}>
                            <item.icon />
                            {item.isSelectedScope && (
                              <span role="img" aria-label="Selected scope" className="size-1.5 shrink-0 rounded-full bg-primary -mr-1" />
                            )}
                            <span className={`${item.comingSoon || typeof item.badge === 'string' ? 'shrink-0' : 'truncate'}`}>
                              {item.name}
                            </span>
                            {typeof item.badge === 'string' && item.badge !== '' && (
                              <Badge variant="subtle" size="sm" className="bg-muted-foreground/10 border-transparent shrink! justify-start">
                                {item.badge}
                              </Badge>
                            )}
                            {typeof item.badge === 'number' && item.badge > 0 && (
                              <Badge variant="failure" className="ml-auto">
                                {item.badge}
                              </Badge>
                            )}
                            {item.comingSoon && (
                              <Badge variant="subtle" size="sm" className="bg-muted-foreground/10 border-transparent shrink! justify-start">
                                Coming soon
                              </Badge>
                            )}
                          </HubNavLink>
                        </SidebarMenuButton>
                        {item.action?.to && (!item.action.requiresIngestionSettingsWrite || canWriteIngestionSettings) && (
                          <SidebarMenuAction onClick={() => navigate(item.action?.to ?? '')} showOnHover>
                            <Plus /> <span className="sr-only">{item.action?.label}</span>
                          </SidebarMenuAction>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          {hasAnyRole(['settings-admin', 'operator-admin']) && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Support">
                <HubNavLink to="/app/support">
                  <LifeBuoy />
                  <span>Support</span>
                </HubNavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {showDemoMode && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname === '/presentation'} tooltip="Demo mode">
                <HubNavLink to="/presentation">
                  <Presentation />
                  <span>Demo mode</span>
                </HubNavLink>
              </SidebarMenuButton>
              {state !== 'collapsed' && (
                <div data-sidebar="menu-action" className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                  <DemoModeSwitch />
                </div>
              )}
            </SidebarMenuItem>
          )}

          <SidebarMenuItem>
            <UserMenu />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail className="my-6" />
    </Sidebar>
  );
}
